// Utility layer (TS-2.5.04) - pure, no side effects. Turns the three non-blocking things a
// successful write can carry alongside it into WarningsBanner items, never a modal:
//   - SaveResult.staleness (StalenessWarning, D-204/D-010: a stale edit is APPLIED and reported,
//     never refused) - "someone else changed this while you were editing, here is what reconciled";
//   - SaveResult.issues (ValidationIssue[], PortfolioDataManager.WriteHelpers.cs's EchoBackDate/
//     EchoBackVocabulary/the reconciliation's per-field "kept the stored value" report) - the
//     messy-date/vocabulary echo-back BRD section 8.1 asks for, and the per-field write-conflict
//     report D-010's reconciliation produces;
//   - ProjectDetail.warnings (DataQualityWarning[]) filtered to DataQualityCodes.DuplicateProjectId
//     - decisions.md D-195's per-claimant info, surfaced on the claimant's own detail screen.
//
// TWO RENDER SITES SHARE ONE `issues` ARRAY, AND EACH ISSUE MUST APPEAR AT EXACTLY ONE OF THEM
// (see this batch's decisions.md entry). DateField already has its own echo slot (the
// `interpretation` prop, wired from `interpretationForField`) for the date-parsing codes below -
// so `issueBannerItems` excludes exactly those codes, and nothing else, from the banner. Every
// other issue - including the reconciliation's per-field "not written" report, which has no field-
// level slot of its own - renders in the banner, grouped by identical (code, message) pairs so a
// save that touches every field of the record does not read as a wall of identical lines.
import type { DataQualityWarning, StalenessWarning, ValidationIssue } from "../../../api/generated/contracts";
import { DataQualityCodes, PortfolioIssueCodes } from "../../../api/generated/contracts";
import type { WarningsBannerItem } from "../../../components/WarningsBanner";
import { strings } from "../../../strings";

const F = strings.projects.detail.fields;
const SW = strings.projects.detail.saveWarnings;

/** Codes PortfolioDataManager.WriteHelpers.cs's EchoBackDate emits - each one is rendered at the
 *  originating DateField's own `interpretation` slot (see ProjectForm.tsx), never in the banner. */
const DATE_ECHO_CODES: readonly string[] = [
  PortfolioIssueCodes.DateEchoBack,
  DataQualityCodes.DateUnparseable,
  DataQualityCodes.PlaceholderStillPresent,
];

/** The field names ValidationIssue.field carries (ProjectRecord's own C# property names, per
 *  FieldDiff's reflection-driven census - decisions.md D-270), mapped to the same human labels
 *  the form itself uses. A field this map does not recognize falls back to its raw wire name
 *  rather than throwing, since a new field arriving before this map is updated must not crash the
 *  banner - it is a defensive fallback, not a claim that the name is presentable as-is. */
const FIELD_LABELS: Readonly<Record<string, string>> = {
  Name: F.name,
  RawTeam: F.team,
  Owner: F.owner,
  Description: F.description,
  RawStatus: F.status,
  RawPercentComplete: F.percentComplete,
  RawOriginalGoLive: F.originalGoLive,
  RawCurrentGoLive: F.currentGoLive,
  BusinessProjectId: F.businessProjectId,
  Subgroup: F.subgroup,
  FeatureDetails: F.featureDetails,
  ProductKey: F.product,
};

/** Bookkeeping members of ProjectRecord an operator never edits directly and this form never
 *  renders a control for - the server's own reconciliation loop walks every member of the record
 *  (D-270's field census), so a defensive filter belongs here regardless of whether the server
 *  is also asked to stop reporting these: an internal member name is never something a user can
 *  act on, and "Key"/"ConcurrencyToken"/"UpdatedAtUtc"/"UpdatedBy" reading as if they were form
 *  fields is its own small instance of the dev-speak this batch's rewrite is otherwise fixing. */
const INTERNAL_FIELDS: ReadonlySet<string> = new Set(["Key", "ConcurrencyToken", "UpdatedAtUtc", "UpdatedBy"]);

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

export function stalenessBannerItems(staleness: StalenessWarning | null): WarningsBannerItem[] {
  if (!staleness || !staleness.isStale) return [];

  const who = staleness.lastChangedBy ?? "another user";
  const when = staleness.lastChangedAtUtc ? new Date(staleness.lastChangedAtUtc).toLocaleString() : "moments ago";
  const fields = staleness.changedFields.length > 0 ? staleness.changedFields.join(", ") : "no other fields";

  return [
    {
      key: "staleness",
      tone: "warning",
      message: `${who} changed this record at ${when} while you were editing. Your save was applied and reconciled with theirs; fields touched by both edits: ${fields}.`,
    },
  ];
}

interface IssueGroup {
  readonly code: string;
  readonly message: string;
  readonly fields: string[];
}

/** Groups issues by identical (code, message) - the reconciliation's "not written" report emits
 *  one ValidationIssue per field with the exact same message text, so this is what collapses a
 *  17-line flood (one per ProjectRecord field) into a single banner line naming every field.
 *  A field name in INTERNAL_FIELDS is dropped rather than collected - see that set's own header. */
function groupByCodeAndMessage(issues: readonly ValidationIssue[]): IssueGroup[] {
  const groups = new Map<string, IssueGroup>();

  for (const issue of issues) {
    const field = issue.field && !INTERNAL_FIELDS.has(issue.field) ? issue.field : null;
    const key = issue.code + " :: " + issue.message;
    const existing = groups.get(key);
    if (existing) {
      if (field && !existing.fields.includes(field)) {
        existing.fields.push(field);
      }
      continue;
    }
    groups.set(key, { code: issue.code, message: issue.message, fields: field ? [field] : [] });
  }

  return [...groups.values()];
}

function messageForGroup(group: IssueGroup): string {
  if (group.code === PortfolioIssueCodes.StoreRefused) {
    return group.fields.length > 0 ? SW.fieldsNotWritten(group.fields.map(fieldLabel)) : SW.storeRefusedGeneric;
  }

  return group.fields.length > 0 ? group.fields.map(fieldLabel).join(", ") + ": " + group.message : group.message;
}

export function issueBannerItems(issues: readonly ValidationIssue[]): WarningsBannerItem[] {
  const bannerIssues = issues.filter((issue) => !DATE_ECHO_CODES.includes(issue.code));

  return groupByCodeAndMessage(bannerIssues).map((group, index) => ({
    key: `issue-${group.code}-${index}`,
    tone: "info",
    message: messageForGroup(group),
  }));
}

export function duplicateBusinessIdBannerItems(warnings: readonly DataQualityWarning[]): WarningsBannerItem[] {
  return warnings
    .filter((warning) => warning.code === DataQualityCodes.DuplicateProjectId)
    .map((warning, index) => ({
      key: `duplicate-${index}`,
      tone: "warning",
      message: warning.message,
    }));
}

/** The echoed interpretation for one field (e.g. "RawCurrentGoLive"), for DateField's
 *  `interpretation` prop - restricted to the date-echo codes so a same-named "not written" issue
 *  (which shares the field but not the code) can never be picked up here instead. */
export function interpretationForField(issues: readonly ValidationIssue[], fieldName: string): string | null {
  const match = issues.find((issue) => issue.field === fieldName && DATE_ECHO_CODES.includes(issue.code));
  return match ? match.message : null;
}
